/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import {
  Flex,
  Select,
  Table,
  type TableProps,
  Switch,
  Tooltip,
  Checkbox,
} from "antd";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  type ValidationError,
  type RatesDosageDataInterface,
  type ProductDetails,
  type PlotTableProps,
  type FieldStandardSeed,
} from "../../../pages/new-trail/types";
import { type FC, useEffect, useState } from "react";
import { InputNumber } from "syngenta-digital-cropwise-react-ui-kit";
import populateRatesDosage from "../../../pages/new-trail/hooks/PopulateRatesDosage";
import { get } from "lodash";
import "./plotTable.less";
import {
  getCircleColor,
  getTrialSeedColor,
  getCircleColorBiologicals,
  getCircleColorNutrient,
} from "./utils";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";
import {
  restrictToIntegersOnly,
  restrictToTwoDecimals,
} from "../../../pages/create-protocol/utils";
import VisibilityIcon from "./VisibilityToggle";
import { INPUT_STATUS } from "../../../pages/constant";
import { calculatePlotAreaHa } from "../../utils/areaCalculation";
import { calculateNutrientAmount } from "../../utils/nutrientCalculation";
import {
  formatWithTwoDecimals,
  roundToTwoDecimals,
} from "../../utils/numberFormatting";
import { InfoCircleOutlined } from "@ant-design/icons";

interface DataType {
  index: number;
  key: React.Key;
  product: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const PlotCellColorBar = ({
  data,
  products,
  ratesDosage,
  children,
  trialGroups,
}: any) => {
  const seedsColor = trialGroups
    ? getTrialSeedColor(
        data?.seedrate ?? {},
        products,
        trialGroups,
        ratesDosage
      )
    : getCircleColor(
        data?.seedrate?.variety,
        data?.seedrate?.rate,
        products,
        null,
        ratesDosage
      );
  return (
    <div
      className="plot-cell-color-bar-container"
      style={{
        background: seedsColor !== "" ? seedsColor : "#DFE2E7",
      }}
    >
      {children}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const PlotCellColorBarNutrient = ({
  data,
  ratesDosage,
  plotKey,
  children,
}: any) => {
  const NutrientColor = getCircleColorNutrient(
    ratesDosage,
    data?.fertiliserRate?.fertiliser_rate,
    undefined,
    plotKey
  );
  return (
    <div
      className="plot-cell-color-bar-container"
      style={{
        background: NutrientColor !== "" ? NutrientColor : "#DFE2E7",
      }}
    >
      {children}
    </div>
  );
};

/* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/naming-convention, camelcase */
function validateNutrients(
  data: RatesDosageDataInterface[],
  productDetails: ProductDetails
): ValidationError[] {
  const errors: ValidationError[] = [];
  if (data) {
    if (!productDetails.nutrients) return errors;
    const { application_range, interval_distance, number_of_intervals } =
      productDetails.nutrients;

    data.forEach((item, index) => {
      const rate = item.fertiliser_rate;
      const path = `data[${index}].fertiliser_rate`;
      const messages: string[] = [];

      if (rate !== undefined) {
        if (rate < application_range.min || rate > application_range.max) {
          messages.push(
            `Rate is not in specified range(${String(
              application_range.min
            )} to ${String(application_range.max)}).`
          );
        }
      }

      if (messages.length > 0) {
        errors.push({ path, messages });
      }
    });

    const uniqueRates = Array.from(
      new Set(
        data
          .map((item) => item.fertiliser_rate)
          .filter((rate): rate is number => rate !== undefined && rate != null)
      )
    ).sort((a, b) => a - b);

    for (let i = 1; i < uniqueRates.length; i++) {
      const prevRate = uniqueRates[i - 1];
      const currRate = uniqueRates[i];
      const path = `data[${data.findIndex(
        (item) => item.fertiliser_rate === currRate
      )}].fertiliser_rate`;
      const messages: string[] = [];

      if (Math.abs(currRate - prevRate) < interval_distance) {
        messages.push(
          `Difference between unique rates (${String(prevRate)} and ${String(
            currRate
          )}) is less than minimum distance (${String(interval_distance)}).`
        );
      }

      if (messages.length > 0) {
        errors.push({ path, messages });
      }
    }

    if (uniqueRates.length < number_of_intervals) {
      errors.push({
        path: "global",
        messages: [
          `Number of distinct rates (${String(
            uniqueRates.length
          )}) is less than required intervals (${String(
            number_of_intervals
          )}).`,
        ],
      });
    }
  }
  return errors;
}

/**
 * SonarCloud friendly helper (no nested ternary, no negated condition).
 */
export { validateNutrients };

export const asArray = <T,>(value: T[] | T | null | undefined): T[] => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

export const isFieldStandardSeedDefined = (
  standard: FieldStandardSeed | undefined
): boolean =>
  (standard?.standard_seed_rate ?? 0) > 0 &&
  typeof standard?.standard_product === "string" &&
  standard.standard_product.trim() !== "" &&
  !!standard?.standard_seed_product_id;

export const getStripStandardRateRow = (
  item: RatesDosageDataInterface,
  standard: FieldStandardSeed,
  isChecked: boolean
): RatesDosageDataInterface => {
  if (isChecked) {
    if (!isFieldStandardSeedDefined(standard)) return item;

    return {
      ...item,
      is_standard: true,
      manual_product_id: item.product_id ?? null,
      manual_variety: item.variety ?? "",
      manual_rate: item.rate ?? null,
      product_id: standard.standard_seed_product_id,
      variety: standard.standard_product,
      rate: standard.standard_seed_rate,
    };
  }

  return {
    ...item,
    is_standard: false,
    product_id: item.manual_product_id ?? null,
    variety: item.manual_variety ?? "",
    rate: item.manual_rate ?? undefined,
  };
};

/**
 * Resyncs a single strip-plot "standard" row to the current field standard
 * seed values, reporting whether anything actually changed. Extracted to a
 * named, top-level function (out of the nested map/map used by the
 * isStripPlot sync effect) to keep that effect's function-nesting depth low
 * (SonarCloud typescript:S2004).
 */
export const syncStripStandardSeedRow = (
  item: RatesDosageDataInterface,
  standard: FieldStandardSeed
): { row: RatesDosageDataInterface; changed: boolean } => {
  if (!item?.is_standard) return { row: item, changed: false };

  const isInSync =
    item.product_id === standard.standard_seed_product_id &&
    item.variety === standard.standard_product &&
    item.rate === standard.standard_seed_rate;

  if (isInSync) return { row: item, changed: false };

  return {
    row: {
      ...item,
      product_id: standard.standard_seed_product_id,
      variety: standard.standard_product,
      rate: standard.standard_seed_rate,
    },
    changed: true,
  };
};

/**
 * Normalizes a single bio-treatment entry's boolean/array shape. Extracted
 * to a named, top-level function (out of the nested map/map/map used by the
 * mount-time bio-treatment normalization effect) to keep that effect's
 * function-nesting depth low (SonarCloud typescript:S2004).
 */
export const normalizeBioTreatmentEntry = (t: any) => ({
  treated: !!t?.treated,
  selected_treatment: t?.selected_treatment || [],
});

export const normalizePlotBioTreatments = (plot: any) => ({
  ...plot,
  bioTreatement: plot.bioTreatement?.map(normalizeBioTreatmentEntry) || [],
});

export const normalizeGroupBioTreatments = (group: any) =>
  group.map(normalizePlotBioTreatments);

interface SeedProductLike {
  id: string;
  variety: string;
  density_options?: number[] | null;
  density_range?: { min: number; max: number } | null;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const PlotTable: FC<PlotTableProps> = ({
  ratesAndDoses,
  productDetails,
  flattenedRates,
  vaildateField,
  from,
  plotKey,
}) => {
  const {
    ratesDosage,
    setRatesDosage,
    newTrialState,
    trialProtocolData,
    setRatesDosageValidation,
    setRatesDosageValidationByType,
    bioApplicationIndex,
  } = useNewTrialState();
  const { t } = useTranslation();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const isStripPlot = newTrialState?.trial_plots?.some(
    (plot: any) => plot.plot?.isStripPlot
  );

  const selectedNutrient =
    trialProtocolData?.product_details?.nutrients?.products?.[0]?.nutrient_code;
  const showNutrientColumns =
    !!selectedNutrient && (ratesDosage?.standard_fertilisers?.length ?? 0) > 0;

  const getErrorMessages = (path: string) =>
    errors.find((err) => err.path === path)?.messages ?? [];

  const setSelectedRow = (selectedRowsList: string[]): void => {
    setRatesDosage(
      populateRatesDosage(
        newTrialState.trial_plots,
        selectedRowsList,
        ratesDosage?.rates_and_dosages,
        ratesDosage?.unit_of_rate,
        ratesDosage.standard_seed_rate,
        ratesDosage.standard_product,
        ratesDosage?.standard_seed_product_id,
        ratesDosage.standard_fertilisers,
        ratesDosage.standard_biological_application_settings,
        ratesDosage.standard_tank_mix_rates
      )
    );
  };

  const checkValidation = (): void => {
    if (!trialProtocolData) return;

    let isSeedPlotValid = true;
    let isNutriendPlotValid = true;
    let isBiologicalPlotValid = true;
    let isValidStandard = true;

    if (trialProtocolData.protocol_type?.includes("seed")) {
      const isSeedType = ratesDosage?.type?.includes("seeds");
      isValidStandard =
        !isSeedType ||
        (ratesDosage?.standard_seed_rate > 0 &&
          typeof ratesDosage?.standard_product === "string" &&
          ratesDosage?.standard_product.trim() !== "");
      isSeedPlotValid = flattenedRates.every(
        (item) =>
          item.variety !== "" &&
          item.rate !== null &&
          item.errors.variety === "" &&
          item.errors.range === ""
      );
    }

    if (trialProtocolData.protocol_type?.includes("nutrient")) {
      const error = validateNutrients(
        ratesDosage.rates_and_dosages.flat(),
        trialProtocolData?.product_details
      );
      isNutriendPlotValid = error.length === 0;
    }

    if (trialProtocolData.protocol_type?.includes("biological")) {
      const num =
        trialProtocolData?.product_details?.biologicals?.applications?.length ??
        1;

      const allPlots =
        ratesDosage?.rates_and_dosages?.flatMap((row: any[]) =>
          Array.isArray(row) ? row : []
        ) ?? [];

      const hasAtLeastOneTreatedPerApplication = Array.from(
        { length: num },
        (_, applicationIndex) =>
          allPlots.some((entry: any) => {
            const treatment = entry?.bioTreatement?.[applicationIndex];

            return treatment?.treated === true;
          })
      ).every(Boolean);

      const allTreatedPlotsHaveTreatment = allPlots.every((entry: any) => {
        if (!entry) return false;

        for (let i = 0; i < num; i++) {
          const treatment = entry?.bioTreatement?.[i];

          if (
            treatment?.treated === true &&
            (!Array.isArray(treatment?.selected_treatment) ||
              treatment.selected_treatment.length === 0)
          ) {
            return false;
          }
        }

        return true;
      });

      isBiologicalPlotValid =
        allPlots.length > 0 &&
        hasAtLeastOneTreatedPerApplication &&
        allTreatedPlotsHaveTreatment;
    }

    const seedStandardValid =
      ratesDosage?.standard_seed_rate > 0 &&
      typeof ratesDosage?.standard_product === "string" &&
      ratesDosage?.standard_product.trim() !== "";

    const nutrientStandardValid =
      Array.isArray(ratesDosage?.standard_fertilisers) &&
      ratesDosage.standard_fertilisers.length > 0 &&
      ratesDosage.standard_fertilisers.every(
        (item: any) => item?.rate > 0 && item?.fertiliser?.trim() !== ""
      );

    const biologicalTreatments =
      trialProtocolData?.product_details?.biologicals?.treatments ?? [];

    const biologicalStandardValid =
      Array.isArray(ratesDosage?.standard_biological_application_settings) &&
      ratesDosage.standard_biological_application_settings.length > 0 &&
      ratesDosage.standard_biological_application_settings.every(
        (setting: any, index: number) => {
          const volume = Number(setting?.tank_application_volume);
          const min = Number(
            biologicalTreatments?.[index]?.tank_mix_rate?.min ?? 0
          );
          const max = Number(
            biologicalTreatments?.[index]?.tank_mix_rate?.max ?? 0
          );

          if (!Number.isFinite(volume) || volume <= 0) return false;
          if (!Number.isFinite(min) || !Number.isFinite(max)) return false;

          return volume >= min && volume <= max;
        }
      );

    if (typeof setRatesDosageValidationByType === "function") {
      setRatesDosageValidationByType({
        seed:
          !trialProtocolData.protocol_type?.includes("seed") ||
          (seedStandardValid && isSeedPlotValid),
        biological:
          !trialProtocolData.protocol_type?.includes("biological") ||
          (biologicalStandardValid && isBiologicalPlotValid),
        nutrient:
          !trialProtocolData.protocol_type?.includes("nutrient") ||
          (nutrientStandardValid && isNutriendPlotValid),
      });
    }

    setRatesDosageValidation(
      isValidStandard &&
        isSeedPlotValid &&
        isNutriendPlotValid &&
        isBiologicalPlotValid
    );
  };

  useEffect(() => {
    if (trialProtocolData?.protocol_type?.includes("nutrient")) {
      setErrors(
        validateNutrients(
          ratesDosage.rates_and_dosages.flat(),
          trialProtocolData?.product_details
        )
      );
    }
  }, [ratesDosage, trialProtocolData]);

  useEffect(() => {
    checkValidation();
  }, [ratesDosage.unit_of_rate, ratesDosage]);

  useEffect(() => {
    if (!isStripPlot || !isFieldStandardSeedDefined(ratesDosage)) return;

    let didChange = false;
    const syncedRatesAndDosages = ratesDosage?.rates_and_dosages?.map(
      (group: any) =>
        group.map((item: any) => {
          const { row, changed } = syncStripStandardSeedRow(item, ratesDosage);
          if (changed) didChange = true;
          return row;
        })
    );

    if (didChange) {
      setRatesDosage({
        ...ratesDosage,
        rates_and_dosages: syncedRatesAndDosages,
      });
    }
  }, [
    isStripPlot,
    ratesDosage.standard_seed_product_id,
    ratesDosage.standard_product,
    ratesDosage.standard_seed_rate,
  ]);

  useEffect(() => {
    setRatesDosage((prev) => ({
      ...prev,
      rates_and_dosages: prev.rates_and_dosages.map(
        normalizeGroupBioTreatments
      ),
    }));
  }, []);

  const handleUpdateValue = (
    selectedProductId: string,
    plotId: number
  ): void => {
    const productsAny = asArray<SeedProductLike>(
      (productDetails as any)?.products
    );
    const selectedProduct = productsAny.find(
      (p) => p?.id === selectedProductId
    );

    setRatesDosage({
      ...ratesDosage,
      rates_and_dosages: ratesDosage.rates_and_dosages.map((outerItem: any) =>
        outerItem.map((innerItem: RatesDosageDataInterface) =>
          innerItem.plot_id === plotId
            ? {
                ...innerItem,
                variety: selectedProduct?.variety ?? "",
                product_id: selectedProductId,
              }
            : innerItem
        )
      ),
    });
  };

  const handleInputValue = (values: number | null, data: any): void => {
    vaildateField(data);
    setRatesDosage({
      ...ratesDosage,
      rates_and_dosages: ratesDosage?.rates_and_dosages?.map(
        (ratesDosagesArray: any) =>
          ratesDosagesArray.map((ratesDosagesItem: any) =>
            ratesDosagesItem?.plot_id === data?.plot_id
              ? { ...ratesDosagesItem, rate: values }
              : ratesDosagesItem
          )
      ),
    });
  };

  const handleFertiliserRateChange = (
    value: number | null,
    data: any
  ): void => {
    setRatesDosage({
      ...ratesDosage,
      rates_and_dosages: ratesDosage?.rates_and_dosages?.map(
        (ratesDosagesArray: any) =>
          ratesDosagesArray.map((item: any) => {
            if (item?.plot_id !== data?.plot_id) return item;
            const total =
              ratesDosage?.standard_fertilisers?.reduce(
                (sum, fert) => sum + (fert?.rate ?? 0),
                0
              ) ?? 0;
            const amount = value && total ? (value / 100) * total : 0;
            return {
              ...item,
              fertiliser_rate: value,
              fertiliser_amount: amount,
            };
          })
      ),
    });
  };

  const handleTreatChange = (
    values: boolean,
    data: any,
    _record?: any,
    _index?: number
  ): void => {
    const updatedRatesDosageMap = ratesDosage?.rates_and_dosages?.map(
      (ratesDosagesArray: any) => {
        return ratesDosagesArray.map((ratesDosagesItem: any) => {
          if (ratesDosagesItem?.plot_id === data?.plot_id) {
            const newTreatment = {
              treated: values,
              selected_treatment: values
                ? ratesDosagesItem?.bioTreatement?.[bioApplicationIndex]
                    ?.selected_treatment || []
                : [],
            };

            const newBioTreatement = [
              ...(ratesDosagesItem?.bioTreatement || []),
            ];
            newBioTreatement[bioApplicationIndex] = newTreatment;

            return {
              ...ratesDosagesItem,
              bioTreatement: newBioTreatement,
            };
          }
          return ratesDosagesItem;
        });
      }
    );

    setRatesDosage({
      ...ratesDosage,
      rates_and_dosages: updatedRatesDosageMap,
    });
  };

  const seedProtocol = trialProtocolData?.product_details?.seeds as any;

  const getSeedRateOptionsForRow = (row: any): number[] => {
    const globalDensityRange = seedProtocol?.global_density_range === true;
    const globalDensityRangeIsNull = seedProtocol?.density_range == null;

    if (globalDensityRange && globalDensityRangeIsNull) {
      const globalOptions = Array.isArray(seedProtocol?.density_options)
        ? seedProtocol.density_options
        : [];
      return globalOptions.filter(
        (x: unknown): x is number => typeof x === "number"
      );
    }

    const protocolProducts: SeedProductLike[] = Array.isArray(
      seedProtocol?.products
    )
      ? seedProtocol.products
      : [];

    const protocolProduct = protocolProducts.find(
      (p) => p?.id && row?.product_id && p.id === row.product_id
    );

    const perProductRangeIsNull = protocolProduct?.density_range == null;

    const productDensityOptions = protocolProduct?.density_options;
    const perProductOptions = Array.isArray(productDensityOptions)
      ? productDensityOptions
      : [];

    if (perProductRangeIsNull && perProductOptions.length > 0) {
      return perProductOptions.filter(
        (x: unknown): x is number => typeof x === "number"
      );
    }

    const rowOptions = Array.isArray(row?.density_options)
      ? row.density_options
      : [];
    return rowOptions.filter(
      (x: unknown): x is number => typeof x === "number"
    );
  };

  const shouldShowSeedRateDropdown = (row: any): boolean => {
    const options = getSeedRateOptionsForRow(row);
    return options.length > 0;
  };

  const seedColumns: TableProps<DataType>["columns"] = [
    {
      title: isStripPlot
        ? "Strip"
        : formatTranslation(t("trials.plotDesign.trialPlots.table.header1")),
      dataIndex: "index",
      width: isStripPlot ? 90 : 60,
      onHeaderCell: () =>
        isStripPlot ? { style: { minWidth: "76px", width: "90px" } } : {},
      onCell: () =>
        isStripPlot ? { style: { minWidth: "76px", width: "90px" } } : {},
      render: (_, record) => {
        const index = record?.index;
        const displayIndex = isStripPlot ? `Strip ${String(index)}` : index;

        return (
          <PlotCellColorBar
            data={record}
            products={(productDetails as any)?.products}
            ratesDosage={ratesAndDoses}
            trialGroups={ratesDosage?.rates_and_dosages}
            plotKey={plotKey}
          >
            <Flex
              align="center"
              className="ml-15"
              style={
                isStripPlot
                  ? {
                      minWidth: "76px",
                      paddingLeft: "8px",
                      paddingRight: "10px",
                    }
                  : { paddingLeft: "8px" }
              }
            >
              <span>{displayIndex}</span>
            </Flex>
          </PlotCellColorBar>
        );
      },
    },
    ...(isStripPlot
      ? [
          {
            title: formatTranslation(
              t("trials.plotDesign.trialPlots.table.stdRateHeader")
            ),
            dataIndex: "stdRate",
            width: 90,
            render: (_data: any, record: any) => {
              const checked = !!record?.product?.is_standard;
              const checkboxDisabled =
                !checked && !isFieldStandardSeedDefined(ratesDosage);

              const handleStandardRateChange = (isChecked: boolean): void => {
                const updatedRatesAndDosages =
                  ratesDosage?.rates_and_dosages?.map(
                    (ratesDosagesArray: any) =>
                      ratesDosagesArray.map((item: any) =>
                        item?.plot_id === record?.product?.plot_id
                          ? getStripStandardRateRow(
                              item,
                              ratesDosage,
                              isChecked
                            )
                          : item
                      )
                  );

                setRatesDosage({
                  ...ratesDosage,
                  rates_and_dosages: updatedRatesAndDosages,
                });
              };

              return (
                <div style={{ paddingLeft: "15px" }}>
                  <Tooltip
                    title={
                      checkboxDisabled
                        ? formatTranslation(
                            t(
                              "trials.plotDesign.trialPlots.tooltip.standardSeedRateRequired"
                            )
                          )
                        : undefined
                    }
                  >
                    <span>
                      <Checkbox
                        checked={checked}
                        disabled={checkboxDisabled}
                        onChange={(e) =>
                          handleStandardRateChange(e.target.checked)
                        }
                      />
                    </span>
                  </Tooltip>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: formatTranslation(t("trials.plotDesign.trialPlots.table.header2")),
      dataIndex: "product",
      render: (data) => {
        const err = vaildateField(data);
        for (const item of flattenedRates) {
          if (item.plot_id === data.plot_id) {
            item.errors = { ...err };
          }
        }

        const productsAny = asArray<SeedProductLike>(
          (productDetails as any)?.products
        );

        return (
          <div>
            {data.is_standard ? (
              <div className="plottable-seelct-product locked-standard-product select-item">
                <span>{data.variety}</span>
              </div>
            ) : (
              <Select
                placeholder={formatTranslation(
                  t("trials.plotDesign.trialPlots.select.placeholder")
                )}
                value={data.product_id ?? undefined}
                className="plottable-seelct-product select-item"
                onChange={(selectedProductId) => {
                  handleUpdateValue(selectedProductId, data.plot_id);
                }}
              >
                {productsAny.length > 0 ? (
                  productsAny.map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.variety}
                    </Select.Option>
                  ))
                ) : (
                  <></>
                )}
              </Select>
            )}
            <div>
              <span
                style={{ display: "flex", marginTop: "4px" }}
                className="error-message"
              >
                {err.range}
              </span>
              <span
                style={{ display: "flex", marginTop: "4px" }}
                className="error-message"
              >
                {err.variety}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: formatTranslation(t("trials.plotDesign.trialPlots.table.header3")),
      dataIndex: "seedrate",
      width: 140,
      render: (data) => {
        const options = getSeedRateOptionsForRow(data);
        const showDropdown = shouldShowSeedRateDropdown(data);

        if (showDropdown) {
          return (
            <Select
              className="seeds-rate-select"
              style={{ width: "100%", height: "39px" }}
              placeholder="Select"
              value={typeof data?.rate === "number" ? data.rate : undefined}
              disabled={!!data.is_standard}
              onChange={(value) => {
                const numeric =
                  typeof value === "number" ? value : Number(value);
                handleInputValue(
                  Number.isFinite(numeric) ? numeric : null,
                  data
                );
              }}
              options={options.map((opt) => ({
                label: String(opt),
                value: opt,
              }))}
            />
          );
        }

        return (
          <InputNumber
            value={data.rate || undefined}
            style={{ height: "39px" }}
            min={0}
            precision={2}
            step={0.01}
            status={(data?.rate ?? 0) < 0 ? INPUT_STATUS.ERROR : ""}
            disabled={!!data.is_standard}
            onChange={(values) => {
              handleInputValue(values, data);
            }}
            onKeyDown={(e) => {
              restrictToTwoDecimals(e);
            }}
            className="input-number seeds-rate-input"
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      width: "20px",
      render: (_data: any, record: any) => {
        return (
          <div>
            <VisibilityIcon
              recordKey={record.key as string}
              isVisible={visibleRows.includes(record.key as string)}
              onToggleVisibility={handleToggleVisibility}
            />
          </div>
        );
      },
    },
  ];

  const getNutrientAmountsForRecord = (record: any) => {
    const fertiliserAmount = record?.product?.fertiliser_amount ?? 0;
    const plotId = record?.product?.plot_id;

    const areaHa = calculatePlotAreaHa(
      plotId,
      newTrialState.trial_plots,
      newTrialState.stripsData,
      plotKey
    );

    return calculateNutrientAmount(
      ratesDosage.standard_fertilisers,
      fertiliserAmount,
      selectedNutrient as string,
      areaHa
    );
  };

  const fertilizerColumns: TableProps<DataType>["columns"] = [
    {
      title: isStripPlot
        ? "Strip"
        : formatTranslation(t("trials.plotDesign.trialPlots.table.header1")),
      dataIndex: "index",
      // width: 60,
      width: isStripPlot ? 90 : 60,
      onHeaderCell: () =>
        isStripPlot ? { style: { minWidth: "76px", width: "90px" } } : {},
      onCell: () =>
        isStripPlot ? { style: { minWidth: "76px", width: "90px" } } : {},
      render: (_, record) => {
        const displayIndex = isStripPlot
          ? `Strip ${String(record.index)}`
          : record.index;
        return (
          <PlotCellColorBarNutrient
            data={record}
            ratesDosage={ratesAndDoses}
            plotKey={plotKey}
          >
            <Flex
              align="center"
              className="ml-15"
              style={
                isStripPlot
                  ? {
                      minWidth: "76px",
                      paddingLeft: "8px",
                      paddingRight: "10px",
                    }
                  : { paddingLeft: "8px" }
              }
            >
              <span>{displayIndex}</span>
            </Flex>
          </PlotCellColorBarNutrient>
        );
      },
    },
    {
      title: "Apply",
      children: [
        {
          title: formatTranslation(
            t("trials.plotDesign.trialPlots.table.header.applyPercent")
          ),
          dataIndex: "fertiliserRate",
          width: 100,
          render: (
            data: RatesDosageDataInterface,
            record: any,
            index: number
          ) => {
            const startingIndex = ratesDosage.rates_and_dosages
              .slice(0, plotKey)
              .reduce((acc, val) => acc + val.length, 0);
            const errorIndex = startingIndex + index;
            const path = `data[${errorIndex}].fertiliser_rate`;
            const errorMessages = getErrorMessages(path);
            return (
              <Flex vertical flex={1}>
                <InputNumber
                  value={data?.fertiliser_rate}
                  min={0}
                  precision={0}
                  step={1}
                  status={
                    (data?.fertiliser_rate ?? 0) < 0 ? INPUT_STATUS.ERROR : ""
                  }
                  onChange={(value) => {
                    handleFertiliserRateChange(value, data);
                  }}
                  onKeyDown={(e) => {
                    restrictToIntegersOnly(e);
                  }}
                  className="input-number"
                />
                {errorMessages.map((message: string) => (
                  <span key={`${path}-${message}`} className="error-message">
                    {message}
                  </span>
                ))}
              </Flex>
            );
          },
        },
      ],
    },
    {
      title: (
        <span>
          Product{" "}
          <Tooltip
            title={formatTranslation(
              t("trials.plotDesign.ratesAndDosage.productTooltip")
            )}
          >
            <InfoCircleOutlined
              style={{ fontSize: "12px", cursor: "pointer", marginLeft: "8px" }}
            />
          </Tooltip>
        </span>
      ),
      children: [
        {
          title: formatTranslation(
            t("trials.plotDesign.trialPlots.table.header.amountKgha")
          ),
          dataIndex: "fertiliserAmount",
          width: 80,
          render: (data: RatesDosageDataInterface) => {
            return (
              <div className="fertilizer-cell-value">
                {formatWithTwoDecimals(
                  roundToTwoDecimals(Number(data?.fertiliser_amount ?? 0))
                )}
              </div>
            );
          },
        },
        {
          title: formatTranslation(
            t("trials.plotDesign.trialPlots.table.header.amountKg")
          ),
          dataIndex: "totalProductKg",
          width: 80,
          render: (_data: RatesDosageDataInterface, record: any) => {
            const plotId = record?.product?.plot_id;
            const fertiliserAmount = record?.product?.fertiliser_amount ?? 0;

            const areaHa = calculatePlotAreaHa(
              plotId,
              newTrialState.trial_plots,
              newTrialState.stripsData,
              plotKey
            );

            const totalKg = fertiliserAmount * areaHa;
            return (
              <div className="fertilizer-cell-value">
                {formatWithTwoDecimals(totalKg)}
              </div>
            );
          },
        },
      ],
    },
    ...(showNutrientColumns
      ? [
          {
            title: (
              <span>
                {`${selectedNutrient} | ${formatTranslation(
                  t(
                    `trials.plotDesign.trialPlots.table.header.nutrient.${selectedNutrient}`
                  )
                )}`}{" "}
                <Tooltip
                  title={formatTranslation(
                    t("trials.plotDesign.ratesAndDosage.nutrientTooltip")
                  )}
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: "12px",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  />
                </Tooltip>
              </span>
            ),
            children: [
              {
                title: formatTranslation(
                  t("trials.plotDesign.trialPlots.table.header.amountKgha")
                ),
                dataIndex: "nutrientPerHa",
                width: 80,
                render: (
                  _data: RatesDosageDataInterface,
                  record: any,
                  _index: number
                ) => (
                  <div className="fertilizer-cell-value">
                    {formatWithTwoDecimals(
                      getNutrientAmountsForRecord(record).perHa
                    )}
                  </div>
                ),
              },
              {
                title: formatTranslation(
                  t("trials.plotDesign.trialPlots.table.header.amountKg")
                ),
                dataIndex: "nutrientTotalKg",
                width: 80,
                render: (
                  _data: RatesDosageDataInterface,
                  record: any,
                  _index: number
                ) => (
                  <div className="fertilizer-cell-value">
                    {formatWithTwoDecimals(
                      getNutrientAmountsForRecord(record).total
                    )}
                  </div>
                ),
              },
            ],
          },
        ]
      : []),
    {
      title: "",
      dataIndex: "",
      width: "20px",
      render: (_data: any, record: any) => {
        return (
          <div>
            <VisibilityIcon
              recordKey={record.key as string}
              isVisible={visibleRows.includes(record.key as string)}
              onToggleVisibility={handleToggleVisibility}
            />
            <></>
          </div>
        );
      },
    },
  ];

  const bioLogicalColumns: TableProps<DataType>["columns"] = [
    {
      title: isStripPlot
        ? "Strip"
        : formatTranslation(t("trials.plotDesign.trialPlots.table.header1")),
      dataIndex: "index",
      width: 80,
      render: (_, record) => {
        const bioTreatment = (record.product as any)?.bioTreatement?.[
          bioApplicationIndex
        ];
        const isTreated = bioTreatment?.treated;
        const bioColor = isTreated
          ? getCircleColorBiologicals(
              bioTreatment,
              bioTreatment?.selected_treatment?.[0] ?? null
            ) || "#DFE2E7"
          : "#DFE2E7";
        const displayIndex = isStripPlot
          ? `Strip ${String(record?.index)}`
          : record?.index;
        return (
          <div className="biological-grid-pad">
            <div
              className="plot-cell-color-bar-biological"
              style={{
                backgroundColor: bioColor,
              }}
            />
            <span className="ml-15">
              <span style={{ paddingLeft: "8px" }}>{displayIndex}</span>
            </span>
          </div>
        );
      },
    },
    {
      title: "Treated?",
      dataIndex: "bioTreatement",
      render: (data: RatesDosageDataInterface, record: any, index: number) => {
        return (
          <Switch
            className="plot-table-switch"
            checked={
              data?.bioTreatement?.[bioApplicationIndex]?.treated ?? false
            }
            value={data?.bioTreatement?.[bioApplicationIndex]?.treated ?? false}
            checkedChildren="Treated"
            unCheckedChildren="Untreated"
            onChange={(checked) => {
              handleTreatChange(checked, data, record, index);
            }}
          />
        );
      },
    },
    {
      title: "Select Treatment",
      dataIndex: "bioTreatement",
      render: (data: RatesDosageDataInterface, record: any, index: number) => {
        const availableTreatments =
          trialProtocolData?.product_details?.biologicals?.treatments?.map(
            (treatment, idx) => ({
              id: ` ${idx}`,
              name: `Treatment ${idx + 1}`,
            })
          ) ?? [];

        const isTreated =
          record.product?.bioTreatement?.[bioApplicationIndex] || {};
        const isTreated1 = isTreated?.treated ?? false;
        const currentTreatmentId =
          data?.bioTreatement?.[bioApplicationIndex]?.selected_treatment?.[0];
        const showError = isTreated1 && !currentTreatmentId;

        return (
          <div className="plot-table-treatment-select-container">
            <Select
              className={`plot-table-select-treatment${
                showError ? "error" : ""
              }`}
              variant="outlined"
              style={{ width: "100%" }}
              placeholder=" Untreated "
              value={
                Array.isArray(
                  data?.bioTreatement?.[bioApplicationIndex]?.selected_treatment
                ) &&
                data?.bioTreatement?.[bioApplicationIndex]
                  ?.selected_treatment &&
                data?.bioTreatement[bioApplicationIndex]?.selected_treatment
                  ?.length > 0
                  ? "Treatment" +
                    String(
                      data?.bioTreatement?.[bioApplicationIndex]
                        ?.selected_treatment[0]
                    )
                  : undefined
              }
              onChange={(value) => {
                const updatedRatesDosageMap =
                  ratesDosage?.rates_and_dosages?.map(
                    (ratesDosagesArray: any) => {
                      return ratesDosagesArray.map((ratesDosagesItem: any) => {
                        if (ratesDosagesItem?.plot_id === data?.plot_id) {
                          const updatedTreatment = {
                            ...ratesDosagesItem?.bioTreatement?.[
                              bioApplicationIndex
                            ],
                            selected_treatment: [value],
                            color: getCircleColorBiologicals(
                              { treated: true },
                              Number(value)
                            ),
                          };
                          if (ratesDosagesItem?.bioTreatement) {
                            ratesDosagesItem.bioTreatement[
                              bioApplicationIndex
                            ] = updatedTreatment;
                            return {
                              ...ratesDosagesItem,
                            };
                          }
                          return {
                            ...ratesDosagesItem,
                            bioTreatement: [updatedTreatment],
                          };
                        }
                        return ratesDosagesItem;
                      });
                    }
                  );
                setRatesDosage({
                  ...ratesDosage,
                  rates_and_dosages: updatedRatesDosageMap,
                });
              }}
              key={isTreated1 ? "treated" : "untreated"}
              disabled={!isTreated.treated}
            >
              {availableTreatments.map((t, idx) => (
                <Select.Option key={idx + 1} value={idx + 1}>
                  Treatment {idx + 1}
                </Select.Option>
              ))}
            </Select>
            {showError && (
              <div className="error-message">
                {formatTranslation(t("trials.selectTreatmentError"))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      width: 20,
      render: (_data: any, record: any) => {
        return (
          <VisibilityIcon
            recordKey={record.key as string}
            isVisible={visibleRows.includes(record.key as string)}
            onToggleVisibility={handleToggleVisibility}
          />
        );
      },
    },
  ];

  const nonSeedColumns =
    from === "biologicals" ? bioLogicalColumns : fertilizerColumns;
  const columns = from === "seeds" ? seedColumns : nonSeedColumns;

  const dataSource: DataType[] = ratesAndDoses?.map((r: any, index: number) => {
    return {
      index: index + 1,
      key: r.plot_id,
      product: r,
      seedrate: r,
      fertiliserRate: r,
      fertiliserAmount: r,
      bioTreatement: r,
      bioTreatmentCol: r,
    };
  });

  const selectedRowKeys = [
    ...new Set(get(ratesDosage, "rates_and_dosages[0][0].collapseValue", [])),
  ];

  const [visibleRows, setVisibleRows] = useState<string[]>(selectedRowKeys);
  const handleToggleVisibility = (recordKey: string) => {
    const newVisibleRows = visibleRows.includes(recordKey)
      ? visibleRows.filter((key) => key !== recordKey)
      : [...visibleRows, recordKey];
    setVisibleRows(newVisibleRows);
    setSelectedRow(newVisibleRows);
  };

  return (
    <Table
      className="trialing-plot-table-container"
      pagination={false}
      columns={columns}
      dataSource={dataSource}
    />
  );
};

export default PlotTable;
